import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import ClockCautionColorIcon from '@flipster/react/icon/generated/ClockCautionColorIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { tradingSessionBunja } from './state';

// info: TradFi 심볼이지만 아직 off-hours 세션에 진입하지 않은 상태 (정보 안내)
// warning: 현재 tradable off-hours 세션에 진입한 상태 (경고)
const variantIcons = {
  info: <InformationCircleIcon size={20} color={token('colors.content.default.level-5')} />,
  warning: <ClockCautionColorIcon size={20} />,
};

/**
 * TradFi 심볼의 off-hours 상태 안내 배너.
 * (Figma "Provide Info and Warning on OI change during off-hours")
 *
 * 디자인은 하나지만 현재 off-hours 상태에 따라 문구/아이콘이 달라진다:
 * - 비 TradFi 심볼: 렌더하지 않음
 * - TradFi & 현재 off-hours 진입(isTradableOffHours): warning 변형
 * - TradFi & 그 외(세션 스케줄만 존재): info 변형
 */
export const OffHoursBanner = () => {
  const t = useTranslations('Trade.r260702');
  const { isTradFiInstrumentAtom, isTradableOffHoursAtom, isTradableAtom } = useBunja(tradingSessionBunja);
  const isTradFi = useAtomValue(isTradFiInstrumentAtom);
  const isOffHoursNow = useAtomValue(isTradableOffHoursAtom);
  const isTradable = useAtomValue(isTradableAtom);

  // non-tradable(Market Suspension)일 땐 off-hours 안내 배너를 숨긴다 — "Trading unavailable" 문구가 대신 표시된다.
  if (!isTradFi || !isTradable) return null;

  const isWarning = isOffHoursNow;
  const icon = isWarning ? variantIcons.warning : variantIcons.info;
  const title = isWarning ? t('Market_is_off_hour_now') : t('During_market_off_hours');
  const description = isWarning ? t('Market_is_off_hour_now_body') : t('During_market_off_hours_body');

  return (
    <div className={hstack({ gap: 8, alignItems: 'center', p: 12, rounded: 8, bg: 'background.default.level-1' })}>
      {icon}
      <div className={vstack({ gap: 2, alignItems: 'flex-start' })}>
        <span className={css({ textStyle: 'Text/M/Strong' })}>{title}</span>
        <span
          className={css({
            textStyle: 'Text/S/Regular',
            color: isWarning ? 'content.default.level-3' : 'content.default.level-2',
          })}
        >
          {description}
        </span>
      </div>
    </div>
  );
};
